<template>
    <div class="student-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && student.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="student"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>

                <div class="card mt-3">
                    <div class="card-body">
                        <h5 class="card-title mb-3">
                            Demographic Questionnaire
                        </h5>
                        <demographic-questionnaire
                            v-model="questionnaire"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            v-if="editMode"
                            :key="informationKey"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="student"
                            :campuses="student.campuses"
                            :tags="student.tags"
                            get-tags-function-name="studentTags/index"
                            @on-tags-change="onTagsChange"
                            @on-campuses-change="onCampusesChange"
                            @on-delete-confirm="onDeleteConfirm"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { required, email, minLength } from 'vuelidate/lib/validators';
import { StatusCodes as HTTP } from 'http-status-codes';
import validatePhoneFormat from '@/helpers/validatePhoneFormat';
import AddEditForm from '@/components/students/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';
import DemographicQuestionnaire from '@/components/students/DemographicQuestionnaire';

export default {
    page: {
        title: 'Manage Student'
    },

    validations: {
        student: {
            firstName: {
                required,
                minLength: minLength(2)
            },
            lastName: {
                required,
                minLength: minLength(2)
            },
            email: {
                required,
                email
            },
            phone: {
                isFormatValid: v => {
                    if (!v?.trim()) {
                        return true;
                    }

                    return validatePhoneFormat(v);
                }
            },
            tz: {
                required
            },
            sex: {},
            membershipStatus: {
                required
            },
            yearOfStudy: {
                isYearValid: v => {
                    if (!v && v !== 0) {
                        return true;
                    }

                    const year = Number(v);

                    return year > 1900 && year < 2100;
                }
            },
            hugs: {
                maxElements: value => value.length <= 2
            },
            stars: {
                required
            }
        },
        questionnaire: {
            activeMiluim: {
                required
            },
            livingInDorms: {
                required
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation,
        DemographicQuestionnaire
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit student' : 'Add student',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Students',
                    to: '/students'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            student: {
                hugs: [],
                tags: [],
                campuses: []
            },
            questionnaire: {},
            informationKey: 0,
            serverErrors: []
        };
    },

    computed: {
        sections() {
            const sections = {
                campuses: true,
                tags: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.student = await this.getStudent(this.id);
                this.questionnaire = { ...this.student };
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addStudent: 'students/store',
            deleteStudent: 'students/destroy',
            getStudent: 'students/show',
            updateStudent: 'students/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const campuses = this.student.campuses.map(({ id }) => id);
            const tags = this.student.tags.map(({ id }) => id);
            const hugs = this.student.hugs.map(({ id }) => id);

            const { activeMiluim, livingInDorms, neighborhood } =
                this.questionnaire;

            const questionnaire = { activeMiluim, livingInDorms, neighborhood };

            const data = {
                ...this.student,
                ...questionnaire,
                campuses,
                tags,
                hugs
            };

            try {
                if (!this.editMode) {
                    const added = await this.addStudent(data);

                    this.$router.push(`/students/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateStudent({
                        id: this.id,
                        data
                    });

                    this.student.updatedAt = updatedAt;

                    this.$toaster('Student updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteStudent(this.id);

                this.$router.push('/students');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onTagsChange(tags) {
            this.student.tags = tags;
            this.informationKey++;
        },

        onCampusesChange(campuses) {
            this.student.campuses = campuses;
            this.informationKey++;
        }
    }
};
</script>
